/* Supporting Libraries */
import type { ComponentType, ReactNode, JSX } from 'react';
import { Navigate, useParams } from 'react-router';
import type { Params } from 'react-router';
import type { IFlagsmithFeature } from 'flagsmith/types';
import { isString } from 'lodash';
import { isNotNil } from '~/utilities/type-guards';
import type { RedirectUnlessAllowedFeatureFlagsProps } from '../components/RedirectUnless';
import {
  RedirectUnlessAllowedParams,
  RedirectUnlessFeatureFlagEnabled,
} from '../components/RedirectUnless';
import type { SupportedFeatureFlags } from '../hooks/useLoadFeatureFlags';
import { useLoadFeatureFlags } from '../hooks/useLoadFeatureFlags';

export type RoutableComponent<TParams> = ComponentType<TParams> & {
  isRoutableComponent: true;
};

/**
 * This method should be used sparingly to mark a component safe for routing. We ideally
 * only want components that are lazy loaded or are composed of components which are lazy
 * loaded. Not doing this will cause bloat accross the application.
 */
export const dangerous_markComponentAsRoutable = <TParams,>(
  component: ComponentType<TParams>,
): RoutableComponent<TParams> => {
  const newComponent = component as unknown as RoutableComponent<TParams>;
  newComponent.isRoutableComponent = true;

  return newComponent;
};

type FlaggedRouteProps<TParams extends object> = {
  flag: SupportedFeatureFlags;
  path?: string;
  checkValue?: (value: IFlagsmithFeature['value']) => boolean;
  props: TParams;
  enabled: RoutableComponent<TParams>;
  disabled: RoutableComponent<TParams>;
};

const FlaggedRoute = <TParams extends object>({
  flag,
  enabled,
  disabled,
  checkValue = () => true,
  props,
}: FlaggedRouteProps<TParams>) => {
  const { flags } = useLoadFeatureFlags([flag]);
  const Component = flags[flag].enabled && checkValue(flags[flag].value) ? enabled : disabled;

  return <Component {...props} />;
};

FlaggedRoute.isRoutableComponent = true;

type RedesignRouteProps<TParams extends object> = Omit<FlaggedRouteProps<TParams>, 'flag'>;
const RedesignRouteBody = <TParams extends object>(
  props: RedesignRouteProps<TParams>,
): JSX.Element => {
  return (
    <FlaggedRoute
      {...props}
      flag="flag_app_redesign_2024"
      checkValue={(flagValue) => {
        if (isString(flagValue) && isString(props.path)) {
          const enabledPaths = JSON.parse(flagValue) as string[];
          return enabledPaths.includes(props.path);
        }

        return false;
      }}
    />
  );
};

export const RedesignRoute = dangerous_markComponentAsRoutable(RedesignRouteBody);

export class RouteDefinition<TRouteProps extends object> {
  public readonly path: string;

  private readonly component: RoutableComponent<TRouteProps>;

  private readonly props: TRouteProps;

  public constructor({
    path,
    component,
    props,
  }: {
    path: string;
    component: RoutableComponent<TRouteProps>;
    props: TRouteProps;
  }) {
    this.path = path;
    this.component = component;
    this.props = props;
  }

  public render(): JSX.Element {
    const Component = this.component;
    return (
      <Component
        path={this.path}
        {...this.props}
      />
    );
  }
}

type NewURLFunction = (params: Readonly<Params>) => string;

export const redirector = <TParams extends object>(
  newURLFunction: NewURLFunction,
): RoutableComponent<TParams> => {
  const Component = (): JSX.Element => {
    const params = useParams();
    const newURL = newURLFunction(params);

    return <Navigate to={newURL} />;
  };

  return dangerous_markComponentAsRoutable(Component);
};

export const redirectUnlessAllowedParams = <TParams extends object>({
  allowedParams,
  to,
  component,
}: {
  allowedParams: Record<string, string[]>;
  to: string;
  component: RoutableComponent<TParams>;
}): RoutableComponent<TParams> => {
  const ComponentToRender = component;
  const Component = (props: TParams): JSX.Element => {
    return (
      <RedirectUnlessAllowedParams
        to={to}
        allowedParams={allowedParams}
      >
        <ComponentToRender {...props} />
      </RedirectUnlessAllowedParams>
    );
  };

  return dangerous_markComponentAsRoutable(Component);
};

type RedirectUnlessAllowedFeatureFlagsPropsWithRoutableChildren<TParams extends object> = Omit<
  RedirectUnlessAllowedFeatureFlagsProps,
  'children'
> & {
  component: RoutableComponent<TParams>;
};

export const redirectUnlessFeatureFlagEnabled = <TParams extends object>({
  to,
  flag,
  component,
  hardRedirect,
  flagConditionToAllow,
}: RedirectUnlessAllowedFeatureFlagsPropsWithRoutableChildren<TParams>): RoutableComponent<TParams> => {
  const ComponentToRender = component;
  const Component = (props: TParams): JSX.Element => {
    return (
      <RedirectUnlessFeatureFlagEnabled
        to={to}
        flag={flag}
        hardRedirect={hardRedirect}
        flagConditionToAllow={flagConditionToAllow}
      >
        <ComponentToRender {...props} />
      </RedirectUnlessFeatureFlagEnabled>
    );
  };

  return dangerous_markComponentAsRoutable(Component);
};

type Layout = RoutableComponent<{ children: ReactNode }>;

export type ScreenDefinition<TProps> = {
  layout?: Layout;
  content: RoutableComponent<TProps>;
};

export const routeViaScreenDefinition = <TProps extends object>(
  screenDefinition: ScreenDefinition<TProps>,
): RoutableComponent<TProps> => {
  const Layout = screenDefinition.layout;
  const Content = screenDefinition.content;

  if (isNotNil(Layout)) {
    const LayoutComponent = (props: TProps) => {
      return (
        <Layout>
          <Content {...props} />
        </Layout>
      );
    };

    return dangerous_markComponentAsRoutable(LayoutComponent);
  }

  return Content;
};
